import { defineMessages } from 'react-intl-next';

export const messages = defineMessages({
  defaultValueInputLabel: {
    id: 'field-settings-menus.default-value-input.label',
    defaultMessage: 'Default value',
    description: 'Label for the input used to set a default value for a field.'
  },
  defaultValueInputHelper: {
    id: 'field-settings-menus.default-value-input.helper',
    defaultMessage: 'Value will be added to new entries',
    description:
      'Helper text shown below a field default value input after a value is entered.'
  },
  defaultValueInputPlaceholder: {
    id: 'field-settings-menus.default-value-input.placeholder',
    defaultMessage: 'Add a default value',
    description: 'Placeholder text for a default value input when no value is set.'
  },
  defaultValueTextPlaceholder: {
    id: 'field-settings-menus.default-value-input.text-placeholder',
    defaultMessage: 'Enter text',
    description: 'Placeholder text for a text field default value input.'
  },
  defaultValueTagPlaceholder: {
    id: 'field-settings-menus.default-value-input.tag-placeholder',
    defaultMessage: 'Select a tag',
    description: 'Placeholder text for a single-select tag field default value input.'
  },
  defaultValueTagsPlaceholder: {
    id: 'field-settings-menus.default-value-input.tags-placeholder',
    defaultMessage: 'Select tags',
    description: 'Placeholder text for a multi-select tag field default value input.'
  },
  defaultValueUserPlaceholder: {
    id: 'field-settings-menus.default-value-input.user-placeholder',
    defaultMessage: 'Select a user',
    description: 'Placeholder text for a single-select user field default value input.'
  },
  defaultValueUsersPlaceholder: {
    id: 'field-settings-menus.default-value-input.users-placeholder',
    defaultMessage: 'Select users',
    description: 'Placeholder text for a multi-select user field default value input.'
  },
  defaultValueJiraPlaceholder: {
    id: 'field-settings-menus.default-value-input.jira-placeholder',
    defaultMessage: 'Add a default Jira link',
    description: 'Placeholder text for a Jira field default value input.'
  },
  defaultValueJiraLinksPlaceholder: {
    id: 'field-settings-menus.default-value-input.jira-links-placeholder',
    defaultMessage: 'Add default Jira links',
    description: 'Placeholder text for a multiple Jira links field default value input.'
  },
  defaultValueEntryLinkPlaceholder: {
    id: 'field-settings-menus.default-value-input.entry-link-placeholder',
    defaultMessage: 'Add a default entry link',
    description: 'Placeholder text for an entry link field default value input.'
  },
  defaultValueEntryLinksPlaceholder: {
    id: 'field-settings-menus.default-value-input.entry-links-placeholder',
    defaultMessage: 'Add default entry links',
    description: 'Placeholder text for a multiple entry links field default value input.'
  },
  entryDetailsValuePlaceholder: {
    id: 'field-settings-menus.entry-details.value-placeholder',
    defaultMessage: 'Select an entry value',
    description: 'Placeholder text for the entry details value picker.'
  },
  numberDefaultValueInputPlaceholder: {
    id: 'field-settings-menus.number.default-value-input.placeholder',
    defaultMessage: 'Enter a number',
    description: 'Placeholder text for the default value input of a number field.'
  },
  attachmentDisplaySizeLabel: {
    id: 'field-settings-menus.attachment-display-size.label',
    defaultMessage: 'Display size',
    description: 'Section label for choosing how attachment field media is displayed.'
  },
  entryLinkDisplaySourceLabel: {
    id: 'field-settings-menus.entry-link-display-source.label',
    defaultMessage: 'Display entry link from',
    description:
      'Section label for choosing which database an entry link field displays entries from.'
  },
  entryBacklinkDisplaySourceLabel: {
    id: 'field-settings-menus.entry-backlink-display-source.label',
    defaultMessage: 'Display entry backlink from',
    description:
      'Section label for choosing which database an entry backlink field displays entries from.'
  },
  entryBacklinkDisplayAsLabel: {
    id: 'field-settings-menus.entry-backlink-display-as.label',
    defaultMessage: 'Display as',
    description:
      'Section label for choosing how an entry backlink field displays linked entries.'
  },
  entryBacklinkDisplayAsEntryLinkOption: {
    id: 'field-settings-menus.entry-backlink-display-as-entry-link.option',
    defaultMessage: 'Entry link',
    description: 'Radio option for displaying entry backlink values as entry links.'
  },
  entryBacklinkDisplayAsNumberOption: {
    id: 'field-settings-menus.entry-backlink-display-as-number.option',
    defaultMessage: 'Number of items linked to entry',
    description:
      'Radio option for displaying entry backlink values as the number of items linked to an entry.'
  },
  entryDetailsDisplaySourceLabel: {
    id: 'field-settings-menus.entry-details-display-source.label',
    defaultMessage: 'Display entry details from',
    description:
      'Section label for choosing which entry link or backlink field an entry details field displays details from.'
  },
  entryDetailsDisplayedLabel: {
    id: 'field-settings-menus.entry-details-displayed.label',
    defaultMessage: 'Entry value',
    description:
      'Section label for choosing which field an entry details field displays from linked entries.'
  },
  jiraDetailsDisplaySourceLabel: {
    id: 'field-settings-menus.jira-details-display-source.label',
    defaultMessage: 'Display Jira link details from',
    description:
      'Section label for choosing which Jira link field a Jira details field displays details from.'
  },
  jiraDetailsDisplayedLabel: {
    id: 'field-settings-menus.jira-details-displayed.label',
    defaultMessage: 'Jira detail displayed',
    description:
      'Section label for choosing which Jira detail a Jira details field displays.'
  },
  pageDetailsSourceFieldLabel: {
    id: 'field-settings-menus.page-details-source-field.label',
    defaultMessage: 'Display detail from',
    description:
      'Section label for choosing which Confluence link field a More details field displays details from.'
  },
  pageDetailsDisplayedLabel: {
    id: 'field-settings-menus.page-details-displayed.label',
    defaultMessage: 'Detail displayed',
    description:
      'Section label for choosing which Confluence detail a More details field displays.'
  },
  confluenceStatusDisplaySourceLabel: {
    id: 'field-settings-menus.confluence-status-display-source.label',
    defaultMessage: 'Display status from',
    description:
      'Section label for choosing which Confluence link field provides statuses.'
  },
  allowStatusEditingSectionLabel: {
    id: 'field-settings-menus.allow-status-editing.section-label',
    defaultMessage: 'Allow editing',
    description: 'Section label for enabling status editing from a database.'
  },
  allowStatusEditingToggleLabel: {
    id: 'field-settings-menus.allow-status-editing-toggle.label',
    defaultMessage: 'Allow status editing',
    description: 'Label for the toggle that enables editing Confluence statuses.'
  },
  confluenceLabelDisplaySourceLabel: {
    id: 'field-settings-menus.confluence-label-display-source.label',
    defaultMessage: 'Display label from',
    description: 'Section label for choosing which Confluence link field provides labels.'
  },
  allowLabelEditingSectionLabel: {
    id: 'field-settings-menus.allow-label-editing.section-label',
    defaultMessage: 'Allow editing',
    description: 'Section label for configuring label editing.'
  },
  allowLabelEditingToggleLabel: {
    id: 'field-settings-menus.allow-label-editing-toggle.label',
    defaultMessage: 'Allow label editing',
    description: 'Label for the toggle that enables editing Confluence labels.'
  },
  hyperlinkDisplayAsLabel: {
    id: 'field-settings-menus.hyperlink-display-as.label',
    defaultMessage: 'Display as',
    description: 'Label for choosing how a Hyperlink field value is displayed.'
  },
  hyperlinkDisplayUrl: {
    id: 'field-settings-menus.hyperlink-display-as.url',
    defaultMessage: 'URL',
    description: 'Option to display a Hyperlink field value as a URL.'
  },
  hyperlinkDisplayInline: {
    id: 'field-settings-menus.hyperlink-display-as.inline',
    defaultMessage: 'Inline',
    description: 'Option to display a Hyperlink field value as an inline Smart Link.'
  },
  pageLinkTypeLabel: {
    id: 'field-settings-menus.page-link-type.label',
    defaultMessage: 'Add link as',
    description:
      'Section label for choosing whether a Confluence link points to existing or newly created content.'
  },
  pageLinkTypeExisting: {
    id: 'field-settings-menus.page-link-type.existing',
    defaultMessage: 'Existing',
    description: 'Option for linking to existing Confluence content.'
  },
  pageLinkTypeCreateNew: {
    id: 'field-settings-menus.page-link-type.create-new',
    defaultMessage: 'New',
    description: 'Option for creating new Confluence content from a link field.'
  },
  pageLinkContentTypeLabel: {
    id: 'field-settings-menus.page-link-content-type.label',
    defaultMessage: 'Content type',
    description: 'Label for choosing the type of Confluence content to create.'
  },
  pageLinkContentLocationLabel: {
    id: 'field-settings-menus.page-link-content-location.label',
    defaultMessage: 'Content location',
    description: 'Label for choosing where new Confluence content is created.'
  },
  pageLinkContentLocationFixedDescription: {
    id: 'field-settings-menus.page-link-content-location.fixed-description',
    defaultMessage: 'New content will be added as child item of selected content',
    description:
      'Description of how new Confluence content is placed when the fixed location option is selected.'
  },
  pageLinkContentLocationDynamicDescription: {
    id: 'field-settings-menus.page-link-content-location.dynamic-description',
    defaultMessage: 'New items will be created as child items from this database',
    description:
      'Description of how new Confluence content is placed when the dynamic location option is selected.'
  },
  pageLinkParentContentLabel: {
    id: 'field-settings-menus.page-link-parent-content.label',
    defaultMessage: 'Parent content',
    description: 'Label for choosing the parent of newly created Confluence content.'
  },
  pageLinkTemplateLabel: {
    id: 'field-settings-menus.page-link-template.label',
    defaultMessage: 'Template',
    description: 'Label for choosing a template for newly created Confluence content.'
  },
  defaultValueModeDatePlaceholder: {
    id: 'field-settings-menus.default-value-mode.date-placeholder',
    defaultMessage: 'Select a date',
    description:
      'Placeholder for the select used to choose the default value mode for a date field.'
  },
  dateTimeFormatLabel: {
    id: 'field-settings-menus.date-time-format.label',
    defaultMessage: 'Time format',
    description: 'Label for selecting the time format for a date field.'
  },
  dateTimeFormat12Hour: {
    id: 'field-settings-menus.date-time-format.12-hour',
    defaultMessage: '12 hour',
    description: 'Option label for using 12-hour time in a date field.'
  },
  dateTimeFormat24Hour: {
    id: 'field-settings-menus.date-time-format.24-hour',
    defaultMessage: '24 hour',
    description: 'Option label for using 24-hour time in a date field.'
  },
  numberSeparatorCommaPeriod: {
    id: 'field-settings-menus.number.separator.comma-period',
    defaultMessage: 'Comma, period',
    description:
      'Option label for using a comma as the thousands separator and a period as the decimal separator.'
  },
  numberSeparatorPeriodComma: {
    id: 'field-settings-menus.number.separator.period-comma',
    defaultMessage: 'Period, comma',
    description:
      'Option label for using a period as the thousands separator and a comma as the decimal separator.'
  },
  numberSeparatorSpaceComma: {
    id: 'field-settings-menus.number.separator.space-comma',
    defaultMessage: 'Space, comma',
    description:
      'Option label for using a space as the thousands separator and a comma as the decimal separator.'
  },
  numberSeparatorSpacePeriod: {
    id: 'field-settings-menus.number.separator.space-period',
    defaultMessage: 'Space, period',
    description:
      'Option label for using a space as the thousands separator and a period as the decimal separator.'
  },
  numberSeparatorNoFormatting: {
    id: 'field-settings-menus.number.separator.no-formatting',
    defaultMessage: 'No formatting',
    description:
      'Legacy option label for displaying a number without a thousands separator.'
  },
  numberSeparatorCommaComma: {
    id: 'field-settings-menus.number.separator.comma-comma',
    defaultMessage: 'Comma, comma',
    description:
      'Legacy option label for using commas as both the thousands and decimal separators.'
  },
  numberSeparatorPeriodPeriod: {
    id: 'field-settings-menus.number.separator.period-period',
    defaultMessage: 'Period, period',
    description:
      'Legacy option label for using periods as both the thousands and decimal separators.'
  },
  defaultValueModeDateTimePlaceholder: {
    id: 'field-settings-menus.default-value-mode.date-time-placeholder',
    defaultMessage: 'Select a date and time',
    description:
      'Placeholder for the select used to choose the default value mode for a date and time field.'
  },
  defaultValueModeCurrentUser: {
    id: 'field-settings-menus.default-value-mode.current-user',
    defaultMessage: 'Current user',
    description:
      'Option label for setting the current user as the default value for a user field.'
  },
  defaultValueModeCurrentDate: {
    id: 'field-settings-menus.default-value-mode.current-date',
    defaultMessage: 'Current date',
    description:
      'Option label for setting the current date as the default value for a date field.'
  },
  defaultValueModeCurrentDateTime: {
    id: 'field-settings-menus.default-value-mode.current-date-time',
    defaultMessage: 'Current date and time',
    description:
      'Option label for setting the current date and time as the default value for a date field.'
  },
  defaultValueModeSpecificUser: {
    id: 'field-settings-menus.default-value-mode.specific-user',
    defaultMessage: 'Specific user',
    description:
      'Option label for choosing a specific user as the default value for a user field.'
  },
  defaultValueModeSpecificDate: {
    id: 'field-settings-menus.default-value-mode.specific-date',
    defaultMessage: 'Specific date',
    description:
      'Option label for choosing a specific date as the default value for a date field.'
  },
  userOptionsSectionLabel: {
    id: 'field-settings-menus.user-options-section.label',
    defaultMessage: 'Options',
    description: 'Section label for notification and multiple-user field options.'
  },
  fieldSettingsNotifyUsersSectionLabel: {
    id: 'field-settings-menus.notify-users-section.label',
    defaultMessage: 'Notify users',
    description: 'Section label for notifying users in the user field settings menu.'
  },
  sendNotificationsToggleLabel: {
    id: 'field-settings-menus.send-notifications-toggle.label',
    defaultMessage: 'Send notifications',
    description: 'Label for the toggle that enables user mention notifications.'
  },
  tagOrderSectionLabel: {
    id: 'field-settings-menus.tag-order.section-label',
    defaultMessage: 'Customize tag order',
    description:
      'Section label for the list where tags are reordered, renamed, added and deleted.'
  },
  tagEditTriggerLabel: {
    id: 'field-settings-menus.tag-order.edit-tag',
    defaultMessage: 'Edit tag {label}',
    description:
      'Accessible label for the tag in the tag list, which opens a popup to rename and recolour it.'
  },
  tagNameInputLabel: {
    id: 'field-settings-menus.tag-order.tag-name',
    defaultMessage: 'Tag name',
    description: 'Label for the input that renames a tag.'
  },
  tagDuplicateError: {
    id: 'field-settings-menus.tag-order.duplicate-error',
    defaultMessage: 'A tag with this name already exists.',
    description: 'Error shown when a tag name duplicates an existing tag name.'
  },
  tagOrderAddTagLabel: {
    id: 'field-settings-menus.tag-order.add-tag',
    defaultMessage: 'Add tag',
    description: 'Label for the button that adds a new tag to the end of the tag list.'
  },
  tagOrderDeleteTagTooltip: {
    id: 'field-settings-menus.tag-order.delete-tag',
    defaultMessage: 'Delete tag',
    description: 'Tooltip for the button that deletes a tag.'
  },
  allowMultiToggleSectionLabel: {
    id: 'field-settings-menus.allow-multi-toggle.section-label',
    defaultMessage: 'Allow multiple',
    description: 'Section label for settings that limit field values.'
  },
  allowMultiToggleLabelTags: {
    id: 'field-settings-menus.allow-multi-toggle-tags.label',
    defaultMessage: 'Allow multiple tags',
    description: 'Label for the toggle that enables multiple tags for a tag/select field.'
  },
  allowMultiToggleLabelUsers: {
    id: 'field-settings-menus.allow-multi-toggle-users.label',
    defaultMessage: 'Allow multiple users',
    description: 'Label for the toggle that enables multiple users for a user field.'
  },
  allowMultiToggleLabelLinks: {
    id: 'field-settings-menus.allow-multi-toggle-links.label',
    defaultMessage: 'Allow multiple links',
    description:
      'Label for the toggle that enables multiple links for a Confluence, Jira, or Entry link field.'
  },
  allowMultiToggleTooltipTags: {
    id: 'field-settings-menus.allow-multi-toggle-tags.tooltip',
    defaultMessage:
      'Allowing multiple tags will prevent or remove grouping by this field.',
    description:
      'Tooltip explaining that enabling multiple tags disables grouping by that field.'
  },
  allowMultiToggleTooltipUsers: {
    id: 'field-settings-menus.allow-multi-toggle-users.tooltip',
    defaultMessage:
      'Allowing multiple users will prevent or remove grouping by this field.',
    description:
      'Tooltip explaining that enabling multiple users disables grouping by that field.'
  }
});
